// SectionB.jsx - Employee Medicals Section with Document Selection
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CiImageOn } from "react-icons/ci";
import { LiaSpinnerSolid } from "react-icons/lia";
import { MdDeleteOutline, MdCancel } from "react-icons/md";
import { FaRegSave } from 'react-icons/fa';
import { IoIosCloudDone } from "react-icons/io";
import { AiOutlineFileDone } from 'react-icons/ai';

const SectionB = ({
  templates,
  selectedCompanyName,
  selectedDepartmentName,
  selectedSheFileId,
  selectedSheFileName,
  selectedSheFileScore,
  selectedCompany,
  selectedDepartment,
  userScore,
  totalCalculatedScore,
  setTotalCalculatedScore,
  setShowToaster,
  openFullScreenImage,
  openPdfInNewTab,
  onSave,
  onRefreshTemplates
}) => {

  /* ---------------------------------------------------------
                      State Management 
  ------------------------------------------------------------*/

  // State Management - Template
  const [templateResponses, setTemplateResponses] = useState([]);
  const [templateResponse, setTemplateResponse] = useState(null);

  // State Management - Employee Document Selection (similar to Section A)
  const [empRelatedDocuments, setEmpRelatedDocuments] = useState({});
  const [empSelectedDocumentData, setEmpSelectedDocumentData] = useState({});

  // Employee-specific state (moved from main component)
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [totalEmployeeScore, setTotalEmployeeScore] = useState(0);
  const [savedEmployeeScores, setSavedEmployeeScores] = useState({});
  const [savedEmployeeSubmissions, setSavedEmployeeSubmissions] = useState({});
  const [employeeFileExpiryDate, setEmployeeFileExpiryDate] = useState({});
  const [employeeErrors, setEmployeeErrors] = useState({});
  const [files, setFiles] = useState({});
  const [previewPhotos, setPreviewPhotos] = useState({});
  const [userTemplateScore, setUserTemplateScore] = useState({});
  const [userTemplatePercentage, setUserTemplatePercentage] = useState({});
  const [errorMessage, setErrorMessage] = useState('');

  /*---------------------------------------------------
                  Use Effect
  ----------------------------------------------------*/

  useEffect(() => {
    const fetchTemplateResponses = async () => {
      const manualTemplate = templates.find(template => 
        template.submission_type === 'Manual' && 
        template.template_reference === "Medical Certificate"
      );
      
      if (manualTemplate && selectedSheFileId && selectedCompany && selectedDepartment) {
        try {
          const user = JSON.parse(sessionStorage.getItem('user'));
          const response = await axios.get(
            `/api/compliance-tracker/consultant-fill-in/section-b-template-responses/${user.user_id}/${selectedSheFileId}/${selectedCompany}/${selectedDepartment}`
          );
          
          setTemplateResponses(response.data);
          
          // Set the single template response for the Medical Certificate template
          const medicalTemplateResponse = response.data.find(tr => 
            tr.template_id === manualTemplate.template_id
          );
          setTemplateResponse(medicalTemplateResponse || null);
          
        } catch (error) {
          console.error('Error fetching template responses:', error);
          setTemplateResponses([]);
          setTemplateResponse(null);
        }
      }
    };

    fetchTemplateResponses();
  }, [templates, selectedSheFileId, selectedCompany, selectedDepartment]);

  // UseEffects (moved from main component)
  useEffect(() => {
    const manualTemplate = templates.find(template => 
      template.submission_type === 'Manual' && 
      template.template_reference === "Medical Certificate"
    );
    
    if (manualTemplate && manualTemplate.template_reference === "Medical Certificate") {
      const newTotal = Object.values(savedEmployeeScores).reduce((sum, score) => sum + parseFloat(score), 0);
      setTotalEmployeeScore(newTotal);
    }
  }, [savedEmployeeScores, templates]);

  useEffect(() => {
    const manualTemplates = templates.filter(template => 
      template.submission_type === 'Manual' && 
      template.template_reference === "Medical Certificate"
    );
    
    if (manualTemplates.length > 0) {
      fetchEmployees();
    }
  }, [templates, selectedCompany, selectedDepartment]);

  // FIXED: Initialize employee selected document data and fetch related documents
  useEffect(() => {
  if (employees.length === 0) {
    console.log('No employees available. Skipping initialization.');
    return;
  }

  console.log('Initializing employee document data for employees:', employees.length);
  
  const initializeEmployeeData = async () => {
    const initialEmpSelectedData = {};
    console.log('Searching for manual template with reference "Medical Certificate"...');
    
    const manualTemplate = templates.find(template => 
      template.submission_type === 'Manual' && 
      template.template_reference === "Medical Certificate"
    );

    if (!manualTemplate) {
      console.log('No manual template found for "Medical Certificate".');
      return;
    }

    console.log('Manual template found:', manualTemplate);

    // First, initialize all employees with default data
    employees.forEach(employee => {
      const compositeKey = `${manualTemplate.template_id}-${employee.employee_id}`;
      console.log(`Initializing data for employee: ${employee.employee_name} ${employee.employee_surname}, compositeKey: ${compositeKey}`);
      
      initialEmpSelectedData[compositeKey] = {
        emp_submission_status: employee.emp_submission_status || 'Not Started',
        emp_template_user_score: employee.emp_template_user_score || 0,
        emp_template_user_percentage: employee.emp_template_user_percentage || 0,
        file_expiry_date: employee.file_expiry_date || null,
        file_period: employee.file_period || null,
        employee_name: employee.employee_name,
        employee_surname: employee.employee_surname,
        employee_id: employee.employee_id,
        compliance_template_submission_id: employee.compliance_template_submission_id,
        qa_status: employee.qa_status || 'Not Started',
        qa_comment: employee.qa_comment || 'No comment',
        emp_file_validity: employee.emp_file_validity || 'Not Started'
      };
    });

    console.log('Initial employee selected data:', initialEmpSelectedData);

    // Set initial data
    setEmpSelectedDocumentData(initialEmpSelectedData);

    // Now fetch related documents for each employee and update selection
    for (const employee of employees) {
      const compositeKey = `${manualTemplate.template_id}-${employee.employee_id}`;
      console.log(`Fetching related documents for employee: ${employee.employee_name} ${employee.employee_surname}, compositeKey: ${compositeKey}`);
      await fetchEmployeeRelatedDocuments(compositeKey, employee.employee_id, manualTemplate.template_id);
    }

    console.log('Finished initializing employee data and fetching related documents.');
  };

  initializeEmployeeData();
}, [employees, templates]);


  /*---------------------------------------------------
                      Functions
  ----------------------------------------------------*/
  
  // FIXED: Function - Fetch related documents with proper auto-selection logic
  const fetchEmployeeRelatedDocuments = async (compositeKey, employeeId, templateId) => {
  try {
    console.log(`Fetching related documents for compositeKey: ${compositeKey}, employeeId: ${employeeId}, templateId: ${templateId}`);
    
    const response = await axios.get(
      `/api/compliance-tracker/consultant-fill-in/section-b/employee/related-template-responses/${employeeId}/${templateId}`
    );
    
    const relatedEmpDocs = response.data;
    console.log(`Found ${relatedEmpDocs.length} related documents for ${compositeKey}:`, relatedEmpDocs);
    
    // Update related documents state
    setEmpRelatedDocuments(prev => ({
      ...prev,
      [compositeKey]: relatedEmpDocs
    }));

    // Auto-selection logic based on number of documents
    if (relatedEmpDocs.length > 0) {
      const latestEmpDoc = relatedEmpDocs[0]; // First item is the most recent
      
      console.log(`Latest document for ${compositeKey}:`, {
        employee_document_id: latestEmpDoc.employee_document_id,
        qa_status: latestEmpDoc.qa_status,
        emp_submission_status: latestEmpDoc.emp_submission_status
      });

      if (relatedEmpDocs.length === 1) {
        console.log(`Auto-selecting single document for ${compositeKey}`);
        setEmpSelectedDocumentData(prev => ({
          ...prev,
          [compositeKey]: latestEmpDoc
        }));
      } else {
        console.log(`Auto-selecting latest document from ${relatedEmpDocs.length} documents for ${compositeKey}`);
        setEmpSelectedDocumentData(prev => ({
          ...prev,
          [compositeKey]: latestEmpDoc
        }));
      }
    } else {
      console.log(`No related documents found for ${compositeKey}, keeping default state`);
    }
    
  } catch (error) {
    console.error(`Error fetching employee related template responses for ${compositeKey}:`, error);
  }
};

  // FIXED: Employee document selection handler
  const handleEmployeeDocumentSelect = (compositeKey, documentId, employee, manualTemplate) => {
    console.log(`Document selection changed for ${compositeKey}: documentId = ${documentId}`);
    
    if (documentId === "") {
      // Reset to new submission state
      console.log(`Resetting to new submission for ${compositeKey}`);
      setEmpSelectedDocumentData(prev => ({
        ...prev,
        [compositeKey]: {
          emp_submission_status: 'Not Started',
          emp_template_user_score: 0,
          emp_template_user_percentage: 0,
          file_expiry_date: null,
          file_period: null,
          employee_name: employee.emp_name,
          employee_surname: employee.employee_surname,
          employee_id: employee.employee_id,
          compliance_template_submission_id: employee.compliance_template_submission_id,
          qa_status: 'Not Started',
          qa_comment: 'No comment',
          emp_file_validity: 'Not Started'
        }
      }));
      handleRemoveFile(compositeKey);
      return;
    }

    const selectedEmpDoc = empRelatedDocuments[compositeKey]?.find(
      doc => doc.employee_document_id === documentId
    );
    
    if (selectedEmpDoc) {
      console.log(`Selected document for ${compositeKey}:`, selectedEmpDoc);
      setEmpSelectedDocumentData(prev => ({
        ...prev,
        [compositeKey]: selectedEmpDoc
      }));
    }
  };

  // Format date functions (similar to Section A)
 const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  
  // If it's already in YYYY-MM-DD format, return as is
  if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dateString;
  }
  
  // If it's a Date object or ISO string, extract just the date part
  const date = new Date(dateString);
  
  // Check if the date is valid before calling toISOString()
  if (isNaN(date.getTime())) {
    console.warn('Invalid date string:', dateString);
    return '';
  }
  
  return date.toISOString().split('T')[0];
};

  const formatDateForDatabase = (date) => {
    if (!date) return null;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  // Employee-specific functions (moved from main component)
  const clearEmployeeError = (compositeId) => {
    setEmployeeErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[compositeId];
      return newErrors;
    });
  };

  const fetchEmployees = async () => {
    const manualTemplate = templates.find(template => 
      template.submission_type === 'Manual' && 
      template.template_reference === "Medical Certificate"
    );
    
    if (!manualTemplate) {
      console.log('No manual template found');
      return;
    }
    
    try {
      setLoadingEmployees(true);
      
      // Much simpler URL
      const response = await axios.get(
        `/compliance-tracker/compliance-tracker-management/consultant-template-fill-in/employees/fetch-employee/${selectedCompany}/${selectedDepartment}?templateReference=${encodeURIComponent(manualTemplate.template_reference)}`
      );
      
      if (response.data.success) {
        setEmployees(response.data.employees);
        console.log(`Loaded ${response.data.count} employees`);
      } else {
        throw new Error(response.data.message);
      }
      
    } catch (error) {
      console.error('Error fetching employees:', error);
      setErrorMessage('Failed to load employees. Please try again.');
      setEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const handleEmployeeExpiryDateChange = (compositeId, e) => {
    const selectedDate = e.target.value;
    setEmployeeFileExpiryDate(prev => ({
      ...prev,
      [compositeId]: selectedDate
    }));
  };

  const handleEmployeeFileChange = (templateId, employeeId, event) => {
    const file = event.target.files[0];
    const compositeId = `${templateId}-${employeeId}`;

    clearEmployeeError(compositeId);
    
    if (file) {
      console.log('File selected:', file);
      
      if (file.size <= 10 * 1024 * 1024) {
        setFiles(prev => ({ 
          ...prev, 
          [compositeId]: file 
        }));
        
        if (file.type.startsWith('image/')) {
          setPreviewPhotos(prev => ({
            ...prev,
            [compositeId]: URL.createObjectURL(file)
          }));
        } else if (file.type === 'application/pdf') {
          setPreviewPhotos(prev => ({
            ...prev,
            [compositeId]: 'PDF_FILE'
          }));
        }
        
        setShowToaster(prev => ({ ...prev, employeeFileChanged: true }));
        
      } else {
        setErrorMessage('File size exceeds 10MB limit.');
        event.target.value = '';
      }
    }
  };

  const handleRemoveFile = (documentId) => {
    setPreviewPhotos(prev => {
      const newPreviewPhotos = { ...prev };
      delete newPreviewPhotos[documentId];
      return newPreviewPhotos;
    });
    
    if (documentId.includes('-')) {
      setSavedEmployeeScores(prev => {
        const newScores = { ...prev };
        delete newScores[documentId];
        return newScores;
      });
    }
  };

  /*----------------------------------------------------
              Functions - Submit Employee File
  ----------------------------------------------------*/
  const handleEmployeeFileSubmit = async (document, employee) => {
  console.log('=== handleEmployeeFileSubmit START ===');
  console.log('Document received:', document);
  console.log('Employee received:', employee);
  
  const compositeId = `${document.template_id}-${employee.employee_id}`;
  console.log('Generated compositeId:', compositeId);
  
  const file = files[compositeId];
  console.log('Current employee files state:', files);
  console.log('File for employee:', compositeId, file);

  clearEmployeeError(compositeId);
  console.log('Cleared errors for compositeId:', compositeId);
  
  if (!file) {
    console.error('No file found for employee:', compositeId);
    console.log('Setting error for missing file');
    setEmployeeErrors(prev => ({
      ...prev,
      [compositeId]: 'Error - Please select a file.'
    }));
    return;
  }

  console.log('Checking expiry date for:', compositeId);
  console.log('Employee file expiry dates:', employeeFileExpiryDate);
  console.log('Expiry date for this employee:', employeeFileExpiryDate[compositeId]);
  
  if (!employeeFileExpiryDate[compositeId]) {
    console.log('No expiry date found, setting error');
    setEmployeeErrors(prev => ({
      ...prev,
      [compositeId]: `Please select an expiry date for ${employee.employee_name} ${employee.employee_surname} before saving.`
    }));
    return;
  }

  try {
    console.log('=== CALCULATION PHASE START ===');
    
    const numberOfEmployees = employees.length;
    console.log('Number of employees:', numberOfEmployees);
    
    const itemTemplateScore = parseFloat(document.template_score) / numberOfEmployees;
    console.log('Template score:', document.template_score);
    console.log('Item template score (per employee):', itemTemplateScore);
    
    // CHANGED: Set itemScore equal to itemTemplateScore (full score when saving)
    const itemScore = itemTemplateScore; // This makes Emp Score = Emp Template Score (5/5)
    console.log('Item score (set to itemTemplateScore):', itemScore);
    
    const allEmployeeScores = { ...savedEmployeeScores };
    console.log('Current saved employee scores:', savedEmployeeScores);
    
    allEmployeeScores[compositeId] = itemScore;
    console.log('Updated employee scores with new score:', allEmployeeScores);
    
    const newTotalEmployeeScore = Object.values(allEmployeeScores)
      .reduce((sum, score) => sum + parseFloat(score || 0), 0);
    console.log('New total employee score:', newTotalEmployeeScore);
    
    const templateTotalScore = Object.values(userScore)
      .reduce((sum, score) => sum + parseFloat(score || 0), 0);
    console.log('Current user scores:', userScore);
    console.log('Template total score:', templateTotalScore);
    
    const newTotalCalculatedScore = templateTotalScore + newTotalEmployeeScore;
    console.log('New total calculated score:', newTotalCalculatedScore);
    
    console.log('=== FORM DATA PREPARATION START ===');
    const formData = new FormData();
    
    // Form data setup
    console.log('Adding SHE file data:', {
      sheFileId: selectedSheFileId,
      sheFileName: selectedSheFileName,
      sheFileScore: selectedSheFileScore
    });
    formData.append('sheFileId', selectedSheFileId);
    formData.append('sheFileName', selectedSheFileName);
    formData.append('sheFileScore', selectedSheFileScore);
    
    // Template Details
    console.log('Adding template details:', {
      templateId: document.template_id,
      templateName: document.template_name,
      templateReference: document.template_reference,
      templateUserScore: newTotalEmployeeScore.toFixed(2),
      templateScore: document.template_score,
      submissionType: document.submission_type
    });
    formData.append('templateId', document.template_id);
    formData.append('templateName', document.template_name);
    formData.append('templateReference', document.template_reference);
    formData.append('templateUserScore', newTotalEmployeeScore.toFixed(2));
    formData.append('templateScore', document.template_score);
    formData.append('submissionType', document.submission_type);
    
    // User details
    const user = JSON.parse(sessionStorage.getItem('user'));
    console.log('User from session storage:', user);
    
    console.log('Adding user details to form data');
    formData.append('filledInByUserId', user.user_id);
    formData.append('filledInByUserType', user.userType);
    formData.append('filledInByUserCompany', user.companyId);
    formData.append('filledInByUserDepartment', user.department_id);
    formData.append('filledInByUserName', user.userName);
    formData.append('filledInByUserSurname', user.userSurname);

    // Company and Department
    console.log('Adding company/department details:', {
      selectedCompanyId: selectedCompany,
      selectedCompanyName: selectedCompanyName,
      selectedDepartmentId: selectedDepartment,
      selectedDepartmentName: selectedDepartmentName
    });
    formData.append('selectedCompanyId', selectedCompany);
    formData.append('selectedCompanyName', selectedCompanyName);
    formData.append('selectedDepartmentId', selectedDepartment);
    formData.append('selectedDepartmentName', selectedDepartmentName);

    // Employee details
    console.log('Adding employee details:', {
      empId: employee.employee_id,
      empName: employee.employee_name,
      empSurname: employee.employee_surname,
      empQty: employee.emp_qty
    });
    formData.append('empId', employee.employee_id);
    formData.append('empName', employee.employee_name);
    formData.append('empSurname', employee.employee_surname);
    formData.append('empQty', employee.emp_qty);
    
    console.log('Adding employee score details:', {
      employeeUserScore: itemScore.toFixed(2),
      employeeItemTemplateScore: itemTemplateScore.toFixed(2)
    });
    formData.append('employeeUserScore', itemScore.toFixed(2));
    formData.append('employeeItemTemplateScore', itemTemplateScore.toFixed(2));
    
    // CHANGED: Calculate percentage (should now be 100% since itemScore = itemTemplateScore)
    const percentage = ((itemScore / itemTemplateScore) * 100).toFixed(2);
    console.log('Calculated percentage:', percentage, '% (itemScore/itemTemplateScore * 100)');
    formData.append('employeeItemPercentage', percentage);

    console.log('Adding file details:', {
      empFileUploadIntervals: document.template_frequency,
      empFileExpiryDate: employeeFileExpiryDate[compositeId],
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    });
    formData.append('empFileUploadIntervals', document.template_frequency);
    formData.append('empFileExpiryDate', employeeFileExpiryDate[compositeId]);
    formData.append('file', file, file.name);

    
    console.log('=== API CALL START ===');
    console.log('Making POST request to: /compliance/consultant-fill-in/save-template-file/employee-response');

    
    // --------------------- API --------------------
    const response = await axios.post(
      '/compliance/consultant-fill-in/save-template-file/employee-response', 
      formData, 
      {
        headers: { 'Content-Type': 'multipart/form-data' }
      }
    );

    console.log('API Response received:', response.data);
    console.log('API Response status:', response.status);

    console.log('=== STATE UPDATES START ===');
    
    // Update states after successful submission
    console.log('Updating savedEmployeeScores with:', allEmployeeScores);
    setSavedEmployeeScores(allEmployeeScores);
    
    console.log('Setting totalEmployeeScore to:', newTotalEmployeeScore);
    setTotalEmployeeScore(newTotalEmployeeScore);
    
    console.log('Setting totalCalculatedScore to:', newTotalCalculatedScore);
    setTotalCalculatedScore(newTotalCalculatedScore);

    console.log('Updating userTemplateScore for compositeId:', compositeId, 'with score:', itemScore);
    setUserTemplateScore(prev => ({
      ...prev,
      [compositeId]: itemScore
    }));
    
    console.log('Updating userTemplatePercentage for compositeId:', compositeId, 'with percentage:', parseFloat(percentage));
    setUserTemplatePercentage(prev => ({
      ...prev,
      [compositeId]: parseFloat(percentage)
    }));

    console.log('Marking employee submission as saved for compositeId:', compositeId);
    setSavedEmployeeSubmissions(prev => ({
      ...prev,
      [compositeId]: true
    }));

    console.log('Updating employee submission status to "Saved" for employee_id:', employee.employee_id);
    setEmployees(prevEmployees => 
      prevEmployees.map(emp => {
        if (emp.employee_id === employee.employee_id) {
          console.log('Found matching employee, updating status from:', emp.emp_submission_status, 'to: Saved');
          return {
            ...emp,
            emp_submission_status: 'Saved'
          };
        }
        return emp;
      })
    );

    console.log('Calling fetchEmployees to refresh data');
    await fetchEmployees();
    
    console.log('Showing success toaster');
    setShowToaster(prev => ({ ...prev, fileSaved: true }));

    if (onSave) {
      console.log('Calling onSave callback');
      await onSave();
    } else {
      console.log('No onSave callback provided');
    }

    if (onRefreshTemplates) {
      console.log('Calling onRefreshTemplates callback');
      await onRefreshTemplates();
    } else {
      console.log('No onRefreshTemplates callback provided');
    }

    console.log('Clearing employee error for compositeId:', compositeId);
    clearEmployeeError(compositeId);

    console.log('=== handleEmployeeFileSubmit SUCCESS ===');

  } catch (error) {
    console.error('=== handleEmployeeFileSubmit ERROR ===');
    console.error('Error object:', error);
    console.error('Error message:', error.message);
    console.error('Error stack:', error.stack);
    
    if (error.response) {
      console.error('API Error Response:', error.response);
      console.error('API Error Status:', error.response.status);
      console.error('API Error Data:', error.response.data);
      console.error('API Error Headers:', error.response.headers);
    } else if (error.request) {
      console.error('No response received:', error.request);
    }
    
    const errorMessage = `Error submitting file for ${employee.employee_name} ${employee.employee_surname}. Please try again.`;
    console.error('Setting error message:', errorMessage);
    
    setEmployeeErrors(prev => ({
      ...prev,
      [compositeId]: errorMessage
    }));
    
    console.log('=== handleEmployeeFileSubmit ERROR END ===');
  }
};

  return (
    <div>
      {templates.filter(template => template.submission_type === 'Manual' && template.template_reference === "Medical Certificate").map((document, index) => (
        
        
        <div key={`manual-${index}`}>
          {/* Section B Header */}
          <div className='complaince-tracker-employees-medical-upload-main-text-container'>
            <div className='complaince-tracker-employees-medical-upload-main-text-details-container'>
              <div>
                <h3>Section B - <span className="secondary-color-paragraph">Employee Medicals</span></h3>
                <p>This section is the template that are all the employees that require medicals at {selectedCompanyName} and site {selectedDepartmentName}.</p>
                <b>Template Details</b>
                <p>Template Name: <span className="secondary-color-paragraph">{document.template_name}</span></p>
                <p>Template Reference: <span className="secondary-color-paragraph">{document.template_reference}</span></p>
                <p>Compliance Template Submission Id: <span className="secondary-color-paragraph">{templateResponse?.compliance_template_submission_id || " No submissions yet. "}</span></p>
              </div>
            </div>

            {/* Template Score Container */}
            <div className='complaince-tracker-employees-medical-upload-main-text-score-container'>
              <div className='complaince-tracker-employees-medical-upload-main-text-score-container-container'>
                <div>
                  <div>
                    <h3>Template Scores</h3>
                  </div>
                  <div>
                    <p><span className="secondary-color-paragraph">{templateResponse?.user_template_score || totalEmployeeScore || 0}/ {document.template_score}</span></p>
                  </div>
                  <div>
                    <p><span className="secondary-color-paragraph">{templateResponse?.user_template_percentage || 0} %</span></p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Employee Table with Document Selection */}
          <div className="manual-upload-employees-table-container">
            {loadingEmployees ? (
              <div className="loading-spinner-container">
                <LiaSpinnerSolid className="lia-spin-solid-icon" />
              </div>
            ) : (
              <table className='compliance-tracker-consultant-fill-in-table'>
                <colgroup>
                  <col style={{ width: "200px" }} />
                  <col style={{ width: "50px" }} />
                  <col style={{ width: "50px" }} />
                  <col style={{ width: "50px" }} />
                  <col style={{ width: "70px" }} />
                  <col style={{ width: "100px" }} />
                  <col style={{ width: "50px" }} />
                  <col style={{ width: "120px" }} />
                  <col style={{ width: "50px" }} />
                  <col style={{ width: "150px" }} />
                  <col style={{ width: "150px" }} />
                  <col style={{ width: "150px" }} />
                  <col style={{ width: "200px" }} />
                </colgroup>

                <thead>
                  <tr>
                    <th>Employee Response Document ID</th>
                    <th>Compliance Sub Id</th>
                    <th>Validity</th>
                    <th>Status</th>
                    <th>Save</th>
                    <th>Emp Name</th>
                    <th>Submit</th>
                    <th>Emp Score</th>
                    <th>Emp Template Score</th>
                    <th>Percentage</th>
                    <th>File Expiry Date</th>
                    <th>Intervals</th>
                    <th>QA Status</th>
                    <th>QA Comment</th>
                  </tr>
                </thead>

                <tbody>
                  {employees.length === 0 ? (
                    <tr>
                      <td colSpan="14" className="no-data-message">
                        <i>No employees found for company {selectedCompanyName} and site {selectedDepartmentName}.</i>
                      </td>
                    </tr>
                  ) : (
                    employees.map((employee, idx) => {
                      const compositeKey = `${document.template_id}-${employee.employee_id}`;
                      const selectedEmpDoc = empSelectedDocumentData[compositeKey] || employee;
                      const relatedEmpDocs = empRelatedDocuments[compositeKey] || [];
                      
                      // FIXED: Improved logic for showing "New Submission" option
                      const hasNoSubmissions = relatedEmpDocs.length === 0;
                      const latestSubmission = relatedEmpDocs.length > 0 ? relatedEmpDocs[0] : null;
                      const latestIsApproved = latestSubmission?.qa_status === 'Approved';
                      const showNewSubmissionOption = hasNoSubmissions || latestIsApproved;
                      
                      console.log(`Rendering row for ${employee.employee_name} ${employee.employee_surname}:`, {
                        compositeKey,
                        relatedDocsCount: relatedEmpDocs.length,
                        selectedDocId: selectedEmpDoc.employee_document_id,
                        showNewSubmissionOption
                      });
                      
                      return (
                        <tr key={idx}>
                          {/* FIXED: Employee Response Document ID Dropdown */}
                          <td>
                            <select 
                              className="template-response-dropdown"
                              value={selectedEmpDoc.employee_document_id || ""}
                              onChange={(e) => handleEmployeeDocumentSelect(compositeKey, e.target.value, employee, document)}
                            >
                              {/* Show "New Submission" option based on improved logic */}
                              {showNewSubmissionOption && (
                                <option value=""> -- New Submission -- </option>
                              )}
                              
                              {/* Show all related documents */}
                              {relatedEmpDocs.map((doc, docIdx) => (
                                <option 
                                  key={doc.employee_document_id} 
                                  value={doc.employee_document_id}
                                >
                                  {docIdx + 1} - {doc.employee_document_id} - {doc.date_submitted?.slice(0, 10)} - {doc.qa_status}
                                </option>
                              ))}
                            </select>
                          </td>

                          <td>{selectedEmpDoc.compliance_template_submission_id}</td>
                          
                          {/* File Validity */}
                          <td>
                            {selectedEmpDoc.emp_file_validity === 'Valid' ? (
                              <div className="saved-status">
                                <IoIosCloudDone /> {selectedEmpDoc.emp_file_validity}
                              </div>
                            ) : selectedEmpDoc.emp_file_validity === 'Expired' ? (
                              <div className="cancelled-status">
                                <MdCancel /> {selectedEmpDoc.emp_file_validity}
                              </div>
                            ) : selectedEmpDoc.emp_file_validity === 'Not Valid' ? (
                              <div className="cancelled-status">
                                <MdCancel /> {selectedEmpDoc.emp_file_validity }
                              </div>
                            ) : (
                              <div className="not-saved-status">
                                <MdCancel /> {'Not Started'}
                              </div>
                            )}
                          </td>
                          
                          <td>
                            {selectedEmpDoc.emp_submission_status === 'Saved' ? (
                              <div className="saved-status">
                                <IoIosCloudDone /> {selectedEmpDoc.emp_submission_status}
                              </div>
                            ) : selectedEmpDoc.emp_submission_status === 'Not Started' ? (
                              <div className="not-saved-status">
                                <MdCancel /> {selectedEmpDoc.emp_submission_status}
                              </div>
                            ) : selectedEmpDoc.emp_submission_status === 'Completed' ? (
                              <div className="completed-status">
                                <AiOutlineFileDone /> {selectedEmpDoc.emp_submission_status}
                              </div>
                            ) : (
                              <div className="not-saved-status">
                                <MdCancel /> {selectedEmpDoc.emp_submission_status || 'Not Started'}
                              </div>
                            )}
                          </td>

                          <td>
                            {selectedEmpDoc.qa_status === 'Approved' || selectedEmpDoc.emp_submission_status === 'Completed' ? (
                              <i>N/A</i>
                            ) : (
                              <FaRegSave 
                                onClick={() => handleEmployeeFileSubmit(document, employee)}
                                className='template-save-icon' 
                              />
                            )}
                          </td>

                          <td>{`${employee.employee_name} ${employee.employee_surname}`}</td>
                          
                          <td>
                            {selectedEmpDoc.qa_status === 'Approved' || selectedEmpDoc.emp_submission_status === 'Completed' ? (
                              <i>N/A</i>
                            ) : (
                              <div className="file-upload-container">
                                <input 
                                  type="file" 
                                  id={`file-upload-${employee.employee_id}-${idx}`}
                                  className="submit-button hidden-file-input"
                                  onChange={(e) => handleEmployeeFileChange(`${document.template_id}`, employee.employee_id, e)}
                                  accept=".png,.jpg,.jpeg,.pdf"
                                />
                                
                                {previewPhotos[compositeKey] && (
                                  <div className='checklist-question-toaster-image-delete-button-container'>
                                    <CiImageOn 
                                      className='checklist-question-toaster-image-icon' 
                                      onClick={() => {
                                        if (previewPhotos[compositeKey] === 'PDF_FILE') {
                                          openPdfInNewTab(files[compositeKey]);
                                        } else {
                                          openFullScreenImage(previewPhotos[compositeKey]);
                                        }
                                      }} 
                                    />
                                    <button 
                                      type='button' 
                                      className='delete-image-button' 
                                      onClick={() => handleRemoveFile(compositeKey)}
                                    > 
                                      <MdDeleteOutline /> 
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}

                            {selectedEmpDoc.qa_status !== 'Approved' && selectedEmpDoc.emp_submission_status !== 'Completed' && employeeErrors[compositeKey] && (
                              <div className="error-text-main-container">
                                {employeeErrors[compositeKey]}
                              </div>
                            )}
                          </td>

                          <td>
                            {selectedEmpDoc.template_response_user_score || selectedEmpDoc.emp_template_user_score || '0'}
                          </td> 

                          {/* Employee Template Score - Always from template response */}
                          <td>
                            {selectedEmpDoc.template_response_score || selectedEmpDoc.emp_template_score || '0'}
                          </td>

                          {/* Employee Percentage - Always from template response */}
                          <td>
                            {`${selectedEmpDoc.template_response_percentage || selectedEmpDoc.emp_template_user_percentage || 0} %`}
                          </td>

                          <td>
                            {selectedEmpDoc.qa_status === 'Approved' || selectedEmpDoc.emp_submission_status === 'Completed' ? (
                              <span>{formatDateForInput(selectedEmpDoc.file_expiry_date) || 'No expiry date set'}</span>
                            ) : (
                              <input
                                type='date'
                                value={employeeFileExpiryDate[compositeKey] ? 
                                  employeeFileExpiryDate[compositeKey] : 
                                  formatDateForInput(selectedEmpDoc.file_expiry_date) || ''
                                }
                                onChange={(e) => handleEmployeeExpiryDateChange(compositeKey, e)}
                                required={true}
                              />
                              
                            )}

                            
                          </td>

                          <td>{document.template_frequency}</td>
                          
                          <td>
                            {selectedEmpDoc.qa_status === 'Approved' ? (
                              <div className="saved-status">
                                <IoIosCloudDone /> {selectedEmpDoc.qa_status}
                              </div>
                            ) : selectedEmpDoc.qa_status === 'Pending' ? (
                              <div className="not-saved-status">
                                <MdCancel /> {selectedEmpDoc.qa_status}
                              </div>
                            ) : selectedEmpDoc.qa_status === 'Completed' ? (
                              <div className="completed-status">
                                <AiOutlineFileDone /> {selectedEmpDoc.qa_status}
                              </div>
                            ) : (
                              <div className="not-saved-status">
                                <MdCancel /> {selectedEmpDoc.qa_status || 'Not Started'}
                              </div>
                            )}
                          </td>
                          
                          <td>{selectedEmpDoc.qa_comment || 'No comment'}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SectionB;